import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { Loader2, CheckCircle, AlertCircle } from 'lucide-react';

// Validation schema
const contactFormSchema = z.object({
  name: z.string().min(2, 'validation.nameMinLength').nonempty('validation.nameRequired'),
  email: z.string().email('validation.emailInvalid').nonempty('validation.emailRequired'),
  company: z.string().optional(),
  messageType: z.enum(['question', 'support', 'registration', 'other'], {
    errorMap: () => ({ message: 'validation.messageTypeRequired' }),
  }),
  message: z
    .string()
    .min(20, 'validation.messageMinLength')
    .max(500, 'validation.messageMaxLength')
    .nonempty('validation.messageRequired'),
});

type ContactFormData = z.infer<typeof contactFormSchema>;

const API_BASE_URL = (() => {
  const env = (import.meta.env.VITE_API_URL ?? '').trim();
  if (typeof window !== 'undefined' && window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
    return '/api/v1';
  }
  return env ? `${env}/api/v1` : '/api/v1';
})();

const RATE_LIMIT_KEY = 'contact_form_last_submit';
const RATE_LIMIT_MS = 5 * 60 * 1000; // 5 minutes

export const ContactForm: React.FC = () => {
  const { t } = useTranslation('portal');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ContactFormData>({
    resolver: zodResolver(contactFormSchema),
  });

  const checkRateLimit = (): boolean => {
    const lastSubmit = localStorage.getItem(RATE_LIMIT_KEY);
    if (!lastSubmit) return true;

    const timeSinceLastSubmit = Date.now() - parseInt(lastSubmit, 10);
    return timeSinceLastSubmit >= RATE_LIMIT_MS;
  };

  const updateRateLimit = () => {
    localStorage.setItem(RATE_LIMIT_KEY, Date.now().toString());
  };

  const onSubmit = async (data: ContactFormData) => {
    // Check rate limit
    if (!checkRateLimit()) {
      setSubmitStatus('error');
      setErrorMessage(t('landing.contact.form.rateLimitError'));
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);
    setErrorMessage('');

    try {
      const response = await fetch(`${API_BASE_URL}/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || errorData.message || 'Submission failed');
      }

      // Success
      setSubmitStatus('success');
      updateRateLimit();
      reset();

      // Auto-hide success message after 5 seconds
      setTimeout(() => {
        setSubmitStatus(null);
      }, 5000);
    } catch (error) {
      console.error('Contact form submission error:', error);
      setSubmitStatus('error');
      setErrorMessage(
        error instanceof Error ? error.message : t('landing.contact.form.error')
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-form-container">
      <form onSubmit={handleSubmit(onSubmit)} className="contact-form" aria-label={t('landing.contact.title')}>
        {/* Name Field */}
        <div className="form-group">
          <label htmlFor="name" className="form-label">
            {t('landing.contact.form.name')} <span className="required">*</span>
          </label>
          <input
            id="name"
            type="text"
            {...register('name')}
            className={`form-input ${errors.name ? 'error' : ''}`}
            placeholder={t('landing.contact.form.namePlaceholder')}
            aria-required="true"
            aria-invalid={errors.name ? 'true' : 'false'}
            aria-describedby={errors.name ? 'name-error' : undefined}
          />
          {errors.name && (
            <p id="name-error" className="form-error" role="alert">
              {t(errors.name.message as string)}
            </p>
          )}
        </div>

        {/* Email Field */}
        <div className="form-group">
          <label htmlFor="email" className="form-label">
            {t('landing.contact.form.email')} <span className="required">*</span>
          </label>
          <input
            id="email"
            type="email"
            {...register('email')}
            className={`form-input ${errors.email ? 'error' : ''}`}
            placeholder={t('landing.contact.form.emailPlaceholder')}
            aria-required="true"
            aria-invalid={errors.email ? 'true' : 'false'}
            aria-describedby={errors.email ? 'email-error' : undefined}
          />
          {errors.email && (
            <p id="email-error" className="form-error" role="alert">
              {t(errors.email.message as string)}
            </p>
          )}
        </div>

        {/* Company Field (Optional) */}
        <div className="form-group">
          <label htmlFor="company" className="form-label">
            {t('landing.contact.form.company')}
          </label>
          <input
            id="company"
            type="text"
            {...register('company')}
            className="form-input"
            placeholder={t('landing.contact.form.companyPlaceholder')}
          />
        </div>

        {/* Message Type Dropdown */}
        <div className="form-group">
          <label htmlFor="messageType" className="form-label">
            {t('landing.contact.form.messageType')} <span className="required">*</span>
          </label>
          <select
            id="messageType"
            {...register('messageType')}
            className={`form-input ${errors.messageType ? 'error' : ''}`}
            aria-required="true"
            aria-invalid={errors.messageType ? 'true' : 'false'}
            aria-describedby={errors.messageType ? 'messageType-error' : undefined}
          >
            <option value="">-- {t('landing.contact.form.messageType')} --</option>
            <option value="question">{t('landing.contact.form.messageTypes.question')}</option>
            <option value="support">{t('landing.contact.form.messageTypes.support')}</option>
            <option value="registration">{t('landing.contact.form.messageTypes.registration')}</option>
            <option value="other">{t('landing.contact.form.messageTypes.other')}</option>
          </select>
          {errors.messageType && (
            <p id="messageType-error" className="form-error" role="alert">
              {t(errors.messageType.message as string)}
            </p>
          )}
        </div>

        {/* Message Textarea */}
        <div className="form-group">
          <label htmlFor="message" className="form-label">
            {t('landing.contact.form.message')} <span className="required">*</span>
          </label>
          <textarea
            id="message"
            {...register('message')}
            className={`form-input form-textarea ${errors.message ? 'error' : ''}`}
            placeholder={t('landing.contact.form.messagePlaceholder')}
            rows={6}
            aria-required="true"
            aria-invalid={errors.message ? 'true' : 'false'}
            aria-describedby={errors.message ? 'message-error' : undefined}
          />
          {errors.message && (
            <p id="message-error" className="form-error" role="alert">
              {t(errors.message.message as string)}
            </p>
          )}
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          className="form-submit"
          disabled={isSubmitting}
          aria-busy={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="animate-spin" size={20} />
              {t('landing.contact.form.submitting')}
            </>
          ) : (
            t('landing.contact.form.submit')
          )}
        </button>

        {/* Success Message */}
        {submitStatus === 'success' && (
          <div className="alert alert-success" role="status" aria-live="polite">
            <CheckCircle size={20} />
            <span>{t('landing.contact.form.success')}</span>
          </div>
        )}

        {/* Error Message */}
        {submitStatus === 'error' && (
          <div className="alert alert-error" role="alert" aria-live="assertive">
            <AlertCircle size={20} />
            <span>{errorMessage || t('landing.contact.form.error')}</span>
          </div>
        )}
      </form>

      <style>{`
        .contact-form-container {
          width: 100%;
        }

        .contact-form {
          background: white;
          padding: 40px;
          border-radius: 12px;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
        }

        .form-group {
          margin-bottom: 24px;
        }

        .form-label {
          display: block;
          font-weight: 600;
          color: #334155;
          margin-bottom: 8px;
          font-size: 15px;
        }

        .required {
          color: #ef4444;
          margin-left: 2px;
        }

        .form-input {
          width: 100%;
          padding: 12px 16px;
          font-size: 16px;
          border: 2px solid #e2e8f0;
          border-radius: 8px;
          transition: all 0.2s;
          font-family: inherit;
          background: white;
        }

        .form-input:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .form-input.error {
          border-color: #ef4444;
        }

        .form-input.error:focus {
          box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
        }

        .form-textarea {
          resize: vertical;
          min-height: 120px;
        }

        .form-error {
          margin-top: 6px;
          font-size: 14px;
          color: #ef4444;
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .form-submit {
          width: 100%;
          padding: 14px 24px;
          font-size: 16px;
          font-weight: 600;
          color: white;
          background: #3b82f6;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }

        .form-submit:hover:not(:disabled) {
          background: #2563eb;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
        }

        .form-submit:disabled {
          background: #94a3b8;
          cursor: not-allowed;
          transform: none;
        }

        .alert {
          margin-top: 20px;
          padding: 14px 18px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          gap: 12px;
          font-size: 15px;
          animation: slideIn 0.3s ease-out;
        }

        .alert-success {
          background: #dcfce7;
          color: #166534;
          border: 1px solid #86efac;
        }

        .alert-error {
          background: #fee2e2;
          color: #991b1b;
          border: 1px solid #fca5a5;
        }

        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }

        .animate-spin {
          animation: spin 1s linear infinite;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .contact-form {
            padding: 24px;
          }

          .form-label {
            font-size: 14px;
          }

          .form-input {
            font-size: 16px; /* Prevents zoom on iOS */
          }
        }

        @media (max-width: 480px) {
          .contact-form {
            padding: 20px;
          }
        }
      `}</style>
    </div>
  );
};

export default ContactForm;
